import {
  Monitor,
  Network,
  Shield,
  Activity,
  Server,
  ShieldCheck,
  Brain,
  Calendar,
  Github,
  Twitter,
  Linkedin,
  Globe,
} from 'lucide-react';

import type {
  Feature,
  Service,
  // PricingPlan,
  FAQ,
  TeamMember,
  ContactInfo,
  NavItem,
  SocialLink,
} from '@/types';

/**
 * Application configuration constants
 */
export const APP_CONFIG = {
  name: 'NeurosEdge',
  tagline: 'The Adaptive Execution Intelligence Engine',
  subHeader:"Scale-First AI is hitting limits. You don’t need bigger models, you need automation that adapts.",
  description: '<strong>NeurosEdge</strong> turns natural language into secure, compliant actions—no hallucinations, no surprises.<br> It executes with precision and reliability, reducing risk and manual toil. And it adapts over time, learning your environment to scale smarter as you grow.',
  url: 'https://NeurosEdge.ai',
  email: 'contact@neurosedge.ai',
  phone: '+1 (312) 625-0977',
} as const;

/**
 * Navigation items for the header
 */
export const NAV_ITEMS: NavItem[] = [
  { label: 'Features', href: '#features' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Integrations', href: '#integrations' },
  { label: 'Enterprise', href: '#enterprise' },
  { label: 'Blog', href: '/blog' },
  { label: 'Contact', href: '#contact' },
];

/**
 * Features data for the features section
 */
export const FEATURES: Feature[] = [
  {
    icon: Monitor,
    title: 'Pre-scripted Automation',
    description: 'Dynamic LLM-generated actions that execute real system commands without manual scripting.',
  },
  {
    icon: Network,
    title: 'Natural Language Interface',
    description: 'Command your infrastructure using plain English. No complex syntax or scripting required.',
  },
  {
    icon: Shield,
    title: 'Unified Execution Layer',
    description: 'Single platform for all operations - from credential rotation to system patching and drift detection.',
  },
  {
    icon: Activity,
    title: 'Proactive Self-healing Logic',
    description: 'Intelligent automation that detects issues and applies fixes autonomously with full audit trails.',
  },
];

/**
 * Services data for the services section
 */
export const SERVICES: Service[] = [
  {
    icon: Server,
    title: 'DevOps Excellence',
    description: 'Streamline your operations with infrastructure automation, CI/CD pipelines, and container orchestration—all built by experienced DevOps engineers.',
  },
  {
    icon: ShieldCheck,
    title: 'Cybersecurity by Design',
    description: 'From network hardening to continuous threat monitoring, our cybersecurity solutions are crafted to protect your systems at every layer.',
  },
  {
    icon: Brain,
    title: 'Advanced LLM Integration',
    description: 'Seamlessly embed large language models into your workflows, enabling intelligent task automation, natural language command processing, and contextual decision-making.',
  },
  {
    icon: Calendar,
    title: 'Auto Appointment Booker',
    description: 'Smart scheduling assistant that automates calendar bookings, client follow-ups, and time-slot optimization for service businesses.',
  },
];

/**
 * Early Access data
 */
export const EARLY_ACCESS = {
  title: 'Get Early Access to NeurosEdge',
  subtitle: 'The First Agent-to-Command Execution Engine Is Live',
  description: 'Built for secure, real-time action — not suggestions. NeurosEdge translates natural language into <strong>system-level execution</strong> with zero scripting, no browser emulators, and full audit controls.',
  note: 'Already in preview across Polaris infrastructure. Now available for partners, customers, analysts, and media',
} as const;

/**
 * What You Can Do data
 */
export const CAPABILITIES = {
  title: 'What You Can Do with NeurosEdge',
  items: [
    '"Patch these 75 servers — now."',
    '"Rotate credentials on preview endpoints."',
    '"Scan for drift and auto-remediate."',
  ],
  footer: 'No tickets. No code generation. Just secure, controlled execution.',
} as const;

/**
 * Automation Features - What Makes NeurosEdge Different
 */
export const AUTOMATION_FEATURES = {
  title: 'What Makes NeurosEdge Different',
  subtitle: 'Others Assist. We Execute.',
  comparison: [
    {
      category: 'Pre-scripted automation',
      others: 'Dynamic LLM-generated actions',
      we: 'Dynamic LLM-generated actions'
    },
    {
      category: 'UI based clicks',
      others: 'Natural language command interface',
      we: 'Natural language command interface'
    },
    {
      category: 'Platform sprawl',
      others: 'Unified execution layer',
      we: 'Unified execution layer'
    },
    {
      category: 'Reactive runbooks',
      others: 'Proactive, self-healing logic',
      we: 'Proactive, self-healing logic'
    },
    {
      category: 'Human-triggered flows',
      others: 'Fully autonomous triggers',
      we: 'Fully autonomous triggers'
    }
  ],
  footer: 'NeurosEdge doesn\'t help you manage your stack. It runs it.'
} as const;

/**
 * Real Command-Level Use Cases
 */
export const USE_CASES = {
  title: 'Real Command-Level Use Cases',
  categories: [
    {
      title: 'Rotate Credentials',
      description: '"Rotate all AWS keys older than 90 days."',
      detail: 'NeurosEdge executes rotation, verifies roles, and logs results.'
    },
    {
      title: 'Patch & Reboot',
      description: '"Apply July OS updates to staging, then reboot clean."',
      detail: 'NeurosEdge executes patch workflow, confirms reboot success, flags exceptions.'
    },
    {
      title: 'Detect Drift',
      description: '"Scan all Linux hosts for config drift from baseline image."',
      detail: 'NeurosEdge compares system state and notifies of variances in seconds.'
    },
    {
      title: 'Activate Devices',
      description: '"Activate 500 new kiosks with latest image + hostname pattern."',
      detail: 'NeurosEdge deploys scripts, configures endpoints, and updates CMDB.'
    }
  ]
} as const;

/**
 * Integration Information
 */
export const INTEGRATIONS = {
  title: 'Integrated With Your Stack',
  subtitle: 'NeurosEdge connects directly with your operational ecosystem:',
  categories: [
    {
      title: 'ServiceNow',
      description: 'for task, incident, CMDB control'
    },
    {
      title: 'Azure AD',
      description: 'secure identity validation'
    },
    {
      title: 'AWS',
      description: 'execute EC2/SSM commands'
    },
    {
      title: 'Slack & Teams',
      description: 'trigger from chat'
    },
    {
      title: 'Terraform + GitHub',
      description: 'infra-as-code audits and deployments'
    }
  ],
  footer: 'No emulators. No API nightmares. Just intelligent execution.'
} as const;

/**
 * Who Should Apply data
 */
export const TARGET_AUDIENCE = {
  title: 'Who Should Apply',
  categories: [
    {
      title: 'Technology Partners / Resellers',
      description: 'Embed AI execution into your hardware, workflow, or compliance bundles.',
    },
    {
      title: 'Enterprise Buyers / Customers',
      description: 'Run a 30–60 day pilot. Validate use cases in your own infrastructure.',
    },
    {
      title: 'Analysts & Researchers',
      description: 'Access technical briefings, platform roadmaps, and AI Ops benchmarks.',
    },
    {
      title: 'Media & Press',
      description: 'Be first to cover the shift from GenAI to Agentic AI in enterprise ops.',
    },
  ],
} as const;

/**
 * Enterprise Readiness Information
 */
export const ENTERPRISE_READINESS = {
  title: 'Enterprise Ready',
  subtitle: 'NeurosEdge is ready for real enterprise work — not just prototypes.',
  features: [
    {
      title: 'SOC2 readiness (Q4)',
      description: 'RBAC & Audit Logging'
    },
    {
      title: 'SSO/SAML',
      description: 'Private LLM or BYOM option'
    },
    {
      title: 'Dedicated tenancy',
      description: 'U.S. based onboarding'
    }
  ]
} as const;

/**
 * FAQ data
 */
export const FAQS: FAQ[] = [
  {
    question: 'How is NeurosEdge different from other automation tools?',
    answer: 'Unlike traditional automation tools that require scripting or UI-based workflows, NeurosEdge executes commands directly from natural language. No tickets, no code generation - just secure, controlled execution.',
  },
  {
    question: 'Is NeurosEdge secure for production environments?',
    answer: 'Yes. NeurosEdge is built with enterprise security in mind, featuring RBAC, audit logging, and SOC2 readiness.',
  },
  {
    question: 'What integrations are supported?',
    answer: 'NeurosEdge integrates with ServiceNow, Azure AD, AWS, Slack & Teams, Terraform, and GitHub. No browser emulators or API nightmares - just intelligent execution.',
  },
  {
    question: 'Can I try NeurosEdge in my environment?',
    answer: 'Yes! We offer 30-60 day pilots for enterprise customers to validate use cases in your own infrastructure. Contact us to get started.',
  },
  {
    question: 'What kind of commands can NeurosEdge execute?',
    answer: 'NeurosEdge can handle credential rotation, server patching, drift detection, device activation, and more. From "Patch these 75 servers now" to "Rotate all AWS keys older than 90 days" - all in natural language.',
  },
];

/**
 * Team members data
 */
export const TEAM_MEMBERS: TeamMember[] = [
  {
    name: 'Norman Moore',
    role: 'Founder & CEO',
    location: 'USA',
    bio: 'Former CTO at CloudSecure with 15+ years in DevOps and cybersecurity. MIT graduate with a passion for AI-assisted infrastructure automation.',
    avatar: '/images/normanmoore.jpg',
    social: {
      linkedin: '#',
      twitter: '#',
      website: '#',
    },
  },
  {
    name: 'Saud Khan',
    role: 'Co-Founder & CTO',
    location: 'Pakistan',
    bio: 'AI researcher and security expert with deep expertise in machine learning. Led development of several open-source security tools and LLM applications.',
    avatar: '/images/saudkhan.jpg',
    social: {
      linkedin: '#',
      github: '#',
      website: '#',
    },
  },
];

/**
 * Contact information
 */
export const CONTACT_INFO: ContactInfo = {
  email: 'contact@neurosedge.ai',
  phone: '+1 (312) 625-0977',
  address: {
    street: '111 N Wabash Ave Suite 1722',
    city: 'Chicago, IL 60602',
    state: 'FL',
    zip: '33563',
  },
};

/**
 * Social media links
 */
export const SOCIAL_LINKS: SocialLink[] = [
  { platform: 'Twitter', url: '#', icon: Twitter },
  { platform: 'LinkedIn', url: '#', icon: Linkedin },
  { platform: 'GitHub', url: '#', icon: Github },
  { platform: 'Website', url: '#', icon: Globe },
];

/**
 * Footer navigation items
 */
export const FOOTER_NAV = {
  product: [
    { label: 'Features', href: '#' },
    // { label: 'Pricing', href: '#' },
    { label: 'Security', href: '#' },
    { label: 'Enterprise', href: '#' },
    { label: 'Changelog', href: '#' },
  ],
  resources: [
    { label: 'Documentation', href: '#' },
    { label: 'API Reference', href: '#' },
    { label: 'Blog', href: '/blog' },
    { label: 'Community', href: '#' },
    { label: 'Support', href: '#' },
    { label: 'Admin', href: '/admin/login' },
  ],
  company: [
    { label: 'About Us', href: '#' },
    { label: 'Careers', href: '#' },
    { label: 'Partners', href: '#' },
    { label: 'Press', href: '#' },
    { label: 'Contact', href: '#' },
  ],
  legal: [
    { label: 'Terms', href: '#' },
    { label: 'Privacy', href: '#' },
    { label: 'Cookies', href: '#' },
  ],
} as const;
